<template>
  <div>
    <div class="text-bold text-body2 text-center">
      {{ $d(effectiveDate, 'longDate') }}
      <span
        v-if="orderItemsMatchPayments"
        class="text-green text-bold"
      >
        <q-icon
          name="check_circle"
          size="sm"
        >
          <q-tooltip class="bg-green">
            Order Items Match Payments
          </q-tooltip>
        </q-icon>
      </span>
      <span
        v-else
        class="text-red text-bold"
      >
        <q-icon
          name="warning"
          size="sm"
        >
          <q-tooltip class="bg-red">
            Error: Order Items Do Not Match Payments
          </q-tooltip>
        </q-icon>
      </span>
    </div>
    <q-markup-table
      class="q-my-md"
      separator="cell"
      flat
      bordered
      dense
    >
      <tbody>
        <tr class="header-row">
          <td>
            Order Items
          </td>
          <td>Time</td>
          <td>Amount</td>
        </tr>
        <tr
          v-for="effectiveDatum in orderItemData"
          :key="effectiveDatum.id"
        >
          <td class="sub-label">
            <div
              class="ellipsis"
              style="max-width: 255px;"
            >
              {{ effectiveDatum.label }}
            </div>
          </td>
          <td class="sub-label">
            <formatted-date
              :value="effectiveDatum.time"
              format="icon"
            />
          </td>
          <td class="cell-value">
            <formatted-number :value="effectiveDatum.value" />
            <q-tooltip
              v-if="effectiveDatum.tooltip"
              :delay="550"
            >
              {{ effectiveDatum.tooltip }}
            </q-tooltip>
          </td>
        </tr>
        <tr
          class="total-row"
        >
          <td>
            Total
          </td>
          <td />
          <td class="cell-value">
            <formatted-number
              :value="$_(orderItemData).map('value').sum()"
            />
          </td>
        </tr>
        <tr><td colspan="3" /></tr>
        <tr class="header-row">
          <td>
            Payments
          </td>
          <td>Time</td>
          <td>Amount</td>
        </tr>
        <tr
          v-for="effectiveDatum in paymentData"
          :key="effectiveDatum.id"
        >
          <td class="sub-label">
            <div
              class="ellipsis"
              style="max-width: 255px;"
            >
              {{ effectiveDatum.label }}
            </div>
          </td>
          <td class="sub-label">
            <formatted-date
              :value="effectiveDatum.time"
              format="icon"
            />
          </td>
          <td class="cell-value">
            <formatted-number :value="effectiveDatum.value" />
            <q-tooltip
              v-if="effectiveDatum.tooltip"
              :delay="550"
            >
              {{ effectiveDatum.tooltip }}
            </q-tooltip>
          </td>
        </tr>
        <tr
          class="total-row"
        >
          <td>
            Total
          </td>
          <td />
          <td class="cell-value">
            <formatted-number
              :value="$_(paymentData).map('value').sum()"
            />
          </td>
        </tr>
        <tr><td colspan="3" /></tr>
        <tr class="header-row">
          <td>
            Payment Reconciliation
          </td>
          <td />
          <td>Amount</td>
        </tr>
        <tr>
          <td class="sub-label">
            <div
              class="ellipsis"
              style="max-width: 255px;"
            >
              Order Item Total
            </div>
          </td>
          <td />
          <td class="cell-value">
            <formatted-number
              :value="$_(orderItemData).map('value').sum()"
            />
          </td>
        </tr>
        <tr>
          <td class="sub-label">
            <div
              class="ellipsis"
              style="max-width: 255px;"
            >
              Payment Total
            </div>
          </td>
          <td />
          <td class="cell-value">
            <formatted-number
              :value="$_(paymentData).map('value').sum()"
            />
          </td>
        </tr>
        <tr
          class="grand-total-row"
        >
          <td>
            <span v-if="orderItemsMatchPayments">
              Order Items Match Payments
            </span>
            <span
              v-else
              class="text-red text-bold"
            >
              <q-icon name="warning" />
              Error: Order Items Do Not Match Payments
            </span>
          </td>
          <td />
          <td class="cell-value">
            <formatted-number
              class="text-red"
              :value="balance"
            />
          </td>
        </tr>
      </tbody>
    </q-markup-table>
  </div>
</template>

<script>

import FormattedDate from 'src/modules/admin/components/FormattedDate.vue'
import FormattedNumber from 'src/modules/admin/components/FormattedNumber'

export default {
  name: 'OrderReconciliationTable',
  components: { FormattedNumber, FormattedDate },
  props: {
    order: {
      type: Object,
      required: true
    },
    effectiveDate: {
      type: Date,
      required: true
    }
  },
  computed: {
    orderItemData () {
      const effectiveData = []

      _.each(this.order.orderItems.data, orderItem => {
        if (orderItem.paidAt && orderItem.paymentDate.getTime() === this.effectiveDate.getTime()) {
          effectiveData.push({
            id: orderItem.id,
            label: orderItem.description,
            time: orderItem.paidAt,
            value: orderItem.reconciliationDisplayTotal,
            tooltip: orderItem.description
          })
        }

        if (orderItem.refundDate && orderItem.refundDate.getTime() === this.effectiveDate.getTime()) {
          effectiveData.push({
            id: -orderItem.id,
            label: `Void: ${orderItem.description}`,
            time: orderItem.voidedAt,
            value: -orderItem.reconciliationDisplayTotal
          })
        }
      })

      return effectiveData
    },
    paymentData () {
      const effectiveData = []

      _.each(this.order.payments.data, payment => {
        if (payment.paymentDate && payment.paymentDate.getTime() === this.effectiveDate.getTime()) {
          effectiveData.push({
            id: payment.id,
            label: payment.label,
            time: payment.paidAt,
            value: payment.amount,
            tooltip: payment.description
          })
        }
      })

      return effectiveData
    },
    balance () {
      const orderItemTotal = _(this.orderItemData).map('value').sum()
      const paymentTotal = _(this.paymentData).map('value').sum()

      let balance = orderItemTotal - paymentTotal

      // Fix js floating point rounding errors
      balance = parseFloat(balance.toFixed(2))

      return balance
    },
    orderItemsMatchPayments () {
      return this.balance === 0
    }
  },
  configuration: ['currentSite']
}

</script>

<style lang="scss">
th,
.bold-label {
  font-weight: bold;
}

.header-row td {
  font-weight: bold;
  border-bottom: solid 1px #444;
}

.sub-label {
  padding-left: 1em;
}

.cell-value {
  text-align: right;
}

.total-row td {
  border-top: solid 1px #444;
  font-weight: bold;
}

.grand-total-row td {
  font-weight: bold;
  border-top: double 3px #444;
}
</style>
